<template>
  <div class="main-content">
    <div class="section__content section__content--p30">
      <div class="container-fluid create-job-container p-b-40">
        <v-card class="p-b-30 p-t-30 p-l-20">
          <div class="el-loading-mask" v-if="loading">
            <div class="el-loading-spinner">
              <svg viewBox="25 25 50 50" class="circular">
                <circle cx="50" cy="50" r="20" fill="none" class="path"></circle>
              </svg>
            </div>
          </div>
          <!-- Card Title -->
          <div class="row">
            <div class="btn-actions-pane-left">
              <h3 class="title-1 p-l-140">
                <v-btn
                  text
                  icon
                  class="m-0"
                  @click="$router.back()">
                  <i class="zmdi zmdi-long-arrow-left"></i>
                </v-btn>
                <span v-if="!isEditing">
                  {{ text('new_distrib_header') }}
                </span>
                <span v-else>
                  {{ text('edit_distrib_header') }} <span class="p-l-20 status--gray"> ID={{ form.id }}</span>
                </span>
              </h3>
            </div>
          </div>
          <!-- Name and Profile -->
          <div class="section">
            <div class="row p-b-10">
              <div class="col-md-2 col-l"></div>
              <div class="col-md-10 section-description">
                {{ text('distrib_name_text') }}
              </div>
            </div>
            <div class="row">
              <div class="col-md-2 col-l"></div>
              <div class="col-md-10">
                <v-text-field
                  v-model="form.name"
                  :label="text('title_label')"
                  :counter="255"
                  :rules="rules.nameRules"
                  required>
                </v-text-field>
              </div>
            </div>
          </div>
          <!-- CDN -->
          <div class="section">
            <div class="row p-b-10">
              <div class="col-md-2 col-l">
                <h2>CDN</h2>
              </div>
              <div class="col-md-10 section-description">
                {{ text('distrib_cdn_text') }}
              </div>
            </div>
            <div class="row">
              <div class="col-md-2 col-l"></div>
              <div class="col-md-10 p-r-0">
                <div class="col-md-12 p-l-0 input-output-type">
                  <v-select
                    v-model="form.cdn"
                    label="Endpoint*"
                    :items="endpoints"
                    item-text="name"
                    item-value="id"
                    @change="onCDNChange">
                  </v-select>
                </div>
              </div>
              <div class="col-md-2 col-l" v-if="whitelist.length"></div>
              <div class="col-md-10 p-r-0" v-if="whitelist.length">
                <span>{{ text('whitlist_list') }}:</span>
                <ul>
                  <li v-for="wl of whitelist" :key="wl.ip">
                    {{ wl.ip }}
                  </li>
                </ul>
              </div>
            </div>
          </div>
          <!-- Channels -->
          <div class="section">
            <div class="row p-b-10">
              <div class="col-md-2 col-l">
                <h2>{{ text('channels') }}</h2>
              </div>
              <div class="col-md-10 section-description">
                {{ text('distrib_channels_text') }}
              </div>
            </div>
            <div class="row">
              <div class="col-md-2 col-l"></div>
              <div class="col-md-10 p-r-0">
                <div class="col-md-12 p-l-0 input-output-type">
                  <v-select
                    multiple
                    v-model="form.channels"
                    :label="text('channels_label')"
                    :items="liveJobs"
                    item-text="name"
                    item-value="id">
                  </v-select>
                </div>
              </div>
            </div>
          </div>
          <!-- Action button -->
          <div class="row text-right p-t-10">
            <div class="col-md-2 col-l"></div>
            <div class="col-md-10">
              <v-btn
                large
                :loading="isSaving"
                :disabled="isSaving || !isFormValid"
                color="success"
                @click="save">
                <span v-if="isEditing">{{ text('save_btn') }}</span>
                <span v-else>{{ text('create_btn') }}</span>
              </v-btn>
            </div>
          </div>
        </v-card>
      </div>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import { JsonAPIEndpointToNorm } from '@/components/cdn/shared/converter';
import { channelDistributionToJsonAPI, processChannelDistribution } from '@/store/converters';

export default {
  created() {
    const args = {
      limit: 100,
      offset: 0,
    };
    this.$store.dispatch('jobs/getLiveJobs', args)
      .then(() => {
        this.$store.dispatch('endpoints/getCDNEndpoints')
          .then((endpoints) => {
            this.endpoints = endpoints.map(e => JsonAPIEndpointToNorm(e));
          })
          .catch((errorMsg) => {
            this.$toasted.error(errorMsg, {
              theme: 'toasted-primary',
              position: 'top-center',
              duration: 10000,
            });
          })
          .finally(() => this.$store.commit('resolvePageLoader'));
      })
      .catch((errorMsg) => {
        this.$store.commit('resolvePageLoader');
        this.$toasted.error(errorMsg, {
          theme: 'toasted-primary',
          position: 'top-center',
          duration: 10000,
        });
      });

    if (this.$route.params.id) {
      this.isEditing = true;
      const cdId = this.$route.params.id;
      this.form.id = cdId;
      this.$store.commit('setPageLoader');
      this.$store.dispatch('channelDistribs/getChannelDistrib', cdId)
        .then((cd) => {
          if (cd) {
            const processed = processChannelDistribution(cd);
            processed.channels = processed.channels.map(c => c.id);
            if (cd.cdn) {
              processed.cdn = cd.cdn.id;
            }
            this.form = Vue.util.extend({}, processed);
          } else {
            this.$router.push({ name: 'DistributionListPage' });
          }
        })
        .catch((errorMsg) => {
          this.$toasted.error(errorMsg, {
            theme: 'toasted-primary',
            position: 'top-center',
            duration: 10000,
          });
          this.$router.push({ name: 'DistributionListPage' });
        })
        .finally(() => this.$store.commit('resolvePageLoader'));
    }
  },
  data() {
    return {
      isSaving: false,
      isEditing: false,
      form: {
        id: 0,
        name: '',
        cdn: 0,
        channels: [],
        urls: [],
      },
      rules: {
        nameRules: [
          v => !!v || this.text('title_req_err'),
          v => (v.length >= 3 && v.length <= 255) || this.text('title_min_err'),
        ],
      },
      inputTypes: ['rtmp pull', 'rtmp push', 'http', 'http+hls', 'astra'],
      profiles: [],
      endpoints: [],
      whitelist: [],
    };
  },
  computed: {
    loading() {
      return this.$store.getters.isDataLoaded;
    },
    isFormValid() {
      if (!(this.form.name.length >= 3 && this.form.name.length <= 255)) {
        return false;
      }
      if (this.form.cdn === 0) {
        return false;
      }
      return this.form.channels.length !== 0;
    },
    liveJobs() {
      return this.$store.getters['jobs/getLiveJobs'];
    },
    dict() {
      return this.$store.getters.dictionary;
    },
  },
  methods: {
    onCDNChange() {
      const cdn = this.endpoints.filter(e => e.id === this.form.cdn);
      if (!cdn.length) {
        this.whitelist = [];
      } else {
        this.whitelist = cdn[0].ipWhitelist;
      }
    },
    save() {
      this.isSaving = true;
      if (!this.isFormValid) return;

      const cd = channelDistributionToJsonAPI(this.form);

      const action = this.isEditing ? 'channelDistribs/updateChannelDistrib' : 'channelDistribs/createChannelDistrib';

      this.$store.dispatch(action, cd)
        .then((result) => {
          this.$router.push({
            name: 'DistributionDetailPage',
            params: {
              id: result.id,
            },
          });
        })
        .catch((errorMsg) => {
          this.isSaving = false;
          this.$toasted.error(errorMsg, {
            theme: 'toasted-primary',
            position: 'top-center',
            duration: 7000,
          });
        });
    },
    text(code) {
      // eslint-disable-next-line no-prototype-builtins
      if (this.dict.hasOwnProperty(code)) {
        return this.dict[code];
      }
      return code;
    },
  },
};
</script>
